import type { AccountInfo } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { loginRequest } from '@/config/authConfig';
import { msalInstance } from '@/config/msalInstance';
import Logger from '@/lib/logger';
import { parseJwt } from '@/utils/string';

export const DEFAULT_TOKEN_REFRESH_TIMEOUT_MS = 30_000;
// Request closures built before a refresh keep the previous ID token for a
// short while. Their 401s reuse the published result instead of starting a
// second forced network refresh.
export const RECENT_REFRESH_REUSE_MS = 60_000;

export interface RefreshedTokens {
  idToken: string;
  accessToken: string;
  accountHomeId: string;
  accountUsername: string;
  originatingIdToken: string | null;
}

interface RefreshAccessTokenOptions {
  signal?: AbortSignal;
  currentIdToken?: string;
  refreshTimeoutMs?: number;
}

type TokenRefreshListener = (tokens: RefreshedTokens) => void;

const tokenRefreshListeners = new Set<TokenRefreshListener>();
const refreshFlights = new Map<string, Promise<RefreshedTokens | null>>();
const recentRefreshes = new Map<
  string,
  { tokens: RefreshedTokens; publishedAt: number; generation: number }
>();
let refreshGeneration = 0;

/**
 * msal-react renders the app before MsalProvider awaits initialize(), and
 * msal-browser rejects silent requests until it resolves. initialize() is
 * idempotent, so waiting here is safe for every caller.
 */
export async function ensureMsalInitialized(): Promise<void> {
  await msalInstance.initialize();
}

export function isInteractionRequiredError(
  error: unknown
): error is InteractionRequiredAuthError {
  return (
    error instanceof InteractionRequiredAuthError ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === 'InteractionRequiredAuthError')
  );
}

function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

function getPreferredAccount(accounts: AccountInfo[]): AccountInfo | null {
  const activeAccount = msalInstance.getActiveAccount();

  if (
    activeAccount &&
    accounts.some(
      (account) => account.homeAccountId === activeAccount.homeAccountId
    )
  ) {
    return activeAccount;
  }

  return accounts[0] ?? null;
}

function selectRefreshAccount(currentIdToken?: string): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    Logger.warn('tokenRefresh: no accounts available for silent refresh');
    return null;
  }

  const preferredAccount = getPreferredAccount(accounts);
  if (!currentIdToken) return preferredAccount;

  const expectedUsername = parseJwt(currentIdToken)?.preferred_username;
  if (!expectedUsername) {
    Logger.warn('tokenRefresh: current ID token has no account identity');
    return null;
  }

  const matchingAccount = accounts.find(
    (account) =>
      normalizeUsername(account.username) ===
      normalizeUsername(expectedUsername)
  );

  if (
    !matchingAccount ||
    preferredAccount?.homeAccountId !== matchingAccount.homeAccountId
  ) {
    Logger.warn('tokenRefresh: current session does not match active account');
    return null;
  }

  return matchingAccount;
}

function abortReason(signal: AbortSignal): unknown {
  return (
    signal.reason ?? new DOMException('The operation was aborted', 'AbortError')
  );
}

function waitForPromiseWithSignal<T>(
  promise: Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortReason(signal));

  return new Promise<T>((resolve, reject) => {
    const handleAbort = () => reject(abortReason(signal));
    signal.addEventListener('abort', handleAbort, { once: true });

    promise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
}

export function subscribeToTokenRefresh(
  listener: TokenRefreshListener
): () => void {
  tokenRefreshListeners.add(listener);

  return () => tokenRefreshListeners.delete(listener);
}

/**
 * Invalidates in-flight refresh publication during logout or an account
 * transition. MSAL acquisition cannot be cancelled, so generation checks keep
 * a late result from restoring a stale application session.
 */
export function invalidateTokenRefresh(): void {
  refreshGeneration += 1;
  refreshFlights.clear();
  recentRefreshes.clear();
}

async function acquireFreshTokens({
  account,
  generation,
  originatingIdToken,
  refreshKey,
  refreshTimeoutMs,
}: {
  account: AccountInfo;
  generation: number;
  originatingIdToken: string | null;
  refreshKey: string;
  refreshTimeoutMs: number;
}): Promise<RefreshedTokens | null> {
  try {
    const response = await waitForPromiseWithSignal(
      ensureMsalInitialized().then(() =>
        msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh: true,
        })
      ),
      AbortSignal.timeout(refreshTimeoutMs)
    );

    const currentAccount = getPreferredAccount(msalInstance.getAllAccounts());
    const responseAccountId = response.account?.homeAccountId;
    const accountChanged =
      generation !== refreshGeneration ||
      currentAccount?.homeAccountId !== account.homeAccountId ||
      (responseAccountId !== undefined &&
        responseAccountId !== account.homeAccountId);

    if (accountChanged) {
      Logger.warn('tokenRefresh: discarding result after account transition');
      return null;
    }

    if (!response.idToken) {
      Logger.warn('tokenRefresh: silent refresh returned no ID token');
      return null;
    }

    const tokens: RefreshedTokens = {
      idToken: response.idToken,
      accessToken: response.accessToken,
      accountHomeId: account.homeAccountId,
      accountUsername: account.username,
      originatingIdToken,
    };

    recentRefreshes.set(refreshKey, {
      tokens,
      publishedAt: Date.now(),
      generation,
    });
    tokenRefreshListeners.forEach((listener) => listener(tokens));
    Logger.info('tokenRefresh: silent refresh succeeded');

    return tokens;
  } catch (error) {
    Logger.warn('tokenRefresh: silent refresh failed', {
      errorCode: (error as { errorCode?: string })?.errorCode,
      message: error instanceof Error ? error.message : String(error),
    });

    // The refresh token is gone or was revoked. Only an interactive sign-in
    // can recover, so callers must be able to tell this apart from a
    // transient failure and go to login without retrying.
    if (isInteractionRequiredError(error)) {
      throw error;
    }

    return null;
  }
}

function getRecentRefresh(
  refreshKey: string,
  account: AccountInfo
): RefreshedTokens | null {
  const recent = recentRefreshes.get(refreshKey);
  if (!recent) return null;

  const isReusable =
    recent.generation === refreshGeneration &&
    recent.tokens.accountHomeId === account.homeAccountId &&
    Date.now() - recent.publishedAt <= RECENT_REFRESH_REUSE_MS;

  if (!isReusable) {
    recentRefreshes.delete(refreshKey);
    return null;
  }

  return recent.tokens;
}

/**
 * Refreshes authentication once per account for concurrent unauthorized
 * requests. Each caller remains bound by its own request cancellation signal,
 * while the shared MSAL operation also has an independent upper bound.
 *
 * Resolves `null` when no refresh is possible right now, and rejects with an
 * InteractionRequiredAuthError when only an interactive sign-in can recover.
 */
export function refreshAccessToken({
  signal,
  currentIdToken,
  refreshTimeoutMs = DEFAULT_TOKEN_REFRESH_TIMEOUT_MS,
}: RefreshAccessTokenOptions = {}): Promise<RefreshedTokens | null> {
  const account = selectRefreshAccount(currentIdToken);
  if (!account) return Promise.resolve(null);

  const refreshKey = `${account.homeAccountId}:${currentIdToken ?? 'no-session-token'}`;
  const recentTokens = getRecentRefresh(refreshKey, account);
  if (recentTokens) {
    return waitForPromiseWithSignal(Promise.resolve(recentTokens), signal);
  }

  let refreshPromise = refreshFlights.get(refreshKey);
  if (!refreshPromise) {
    const generation = refreshGeneration;
    const flight = acquireFreshTokens({
      account,
      generation,
      originatingIdToken: currentIdToken ?? null,
      refreshKey,
      refreshTimeoutMs,
    });
    refreshPromise = flight;
    refreshFlights.set(refreshKey, flight);

    const removeFlight = () => {
      if (refreshFlights.get(refreshKey) === flight) {
        refreshFlights.delete(refreshKey);
      }
    };
    // Both handlers: a rejected flight must be removed too, and must not
    // surface as an unhandled rejection from this bookkeeping chain.
    void flight.then(removeFlight, removeFlight);
  }

  return waitForPromiseWithSignal(refreshPromise, signal);
}
